import { Certificate, RequestedDoc } from "../../certificates/models/certificates.interfaces";
import { log, logE } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { TUser } from "../../shared/types";
import {
  DocumentExtractionTaskAccepted,
  DocumentExtractionTaskStatus,
  DocumentExtractionTaskStatusResponse,
  DocumentExtractionTaskProgress,
  DocumentMetadataExtract,
  RequestDocumentDto,
  Vessel,
} from "../models/documents.models";
import type { AxiosResponse } from "axios";

const DOCUMENT_EXTRACTION_POLL_INTERVAL_MS = 2000;
const TERMINAL_EXTRACTION_TASK_STATUSES: ReadonlySet<DocumentExtractionTaskStatus> =
  new Set<DocumentExtractionTaskStatus>(["SUCCEEDED", "FAILED", "CANCELLED", "EXPIRED"]);

const isDocumentExtractionTaskAccepted = (value: unknown): value is DocumentExtractionTaskAccepted => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DocumentExtractionTaskAccepted>;
  return candidate.success === 202 && typeof candidate.taskId === "string" && typeof candidate.s3Path === "string";
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (!signal?.aborted) return;
  const error = new Error("Document extraction was cancelled.");
  error.name = "AbortError";
  throw error;
};

const waitForNextPoll = async (signal?: AbortSignal): Promise<void> => {
  throwIfAborted(signal);
  await new Promise<void>((resolve, reject) => {
    let timeout: number | undefined;
    function cleanup() {
      if (timeout !== undefined) window.clearTimeout(timeout);
      signal?.removeEventListener("abort", abort);
    }
    function abort() {
      cleanup();
      const error = new Error("Document extraction was cancelled.");
      error.name = "AbortError";
      reject(error);
    }

    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) {
      abort();
      return;
    }

    timeout = window.setTimeout(() => {
      cleanup();
      resolve();
    }, DOCUMENT_EXTRACTION_POLL_INTERVAL_MS);
  });
};

const cancelDocumentExtractionTask = async (taskId: string): Promise<void> => {
  log("SERV - cancelDocumentExtractionTask", { taskId });
  try {
    await api.post(`${apiUrl}/processing-tasks/${taskId}/cancel`);
  } catch (error) {
    logE(new Error("SERV - cancelDocumentExtractionTask - failed"));
  }
};

const waitForDocumentExtraction = async (
  acceptedTask: DocumentExtractionTaskAccepted,
  signal?: AbortSignal,
  onProgress?: (progress: DocumentExtractionTaskProgress) => void,
): Promise<DocumentMetadataExtract> => {
  const deadlineMs = Date.parse(acceptedTask.deadlineAt);
  const startedAt = Date.now();
  log("SERV - waitForDocumentExtraction", { taskId: acceptedTask.taskId });

  while (true) {
    throwIfAborted(signal);
    const response = await api.get<DocumentExtractionTaskStatusResponse>(
      `${apiUrl}/processing-tasks/${acceptedTask.taskId}`,
      { signal },
    );
    const task = response.data;
    onProgress?.(task.progress);

    if (task.status === "SUCCEEDED" && task.result) {
      log("SERV - waitForDocumentExtraction - completed", { durationMs: Date.now() - startedAt });
      return {
        ...task.result,
        s3Path: task.s3Path,
      };
    }

    if (TERMINAL_EXTRACTION_TASK_STATUSES.has(task.status) || Date.now() >= deadlineMs) {
      log("SERV - waitForDocumentExtraction - terminal", { status: task.status, expired: Date.now() >= deadlineMs });
      if (Date.now() >= deadlineMs && !TERMINAL_EXTRACTION_TASK_STATUSES.has(task.status)) {
        await cancelDocumentExtractionTask(acceptedTask.taskId);
      }
      return {
        s3Path: task.s3Path,
        type: `task-${Date.now() >= deadlineMs ? "expired" : task.status.toLowerCase()}`,
      };
    }

    await waitForNextPoll(signal);
  }
};

const normalizeExtractedDates = (extract: DocumentMetadataExtract): DocumentMetadataExtract => {
  if (extract.validityStartDate)
    extract.validityStartDate.value = extract.validityStartDate?.value
      ? new Date(extract.validityStartDate.value)
      : extract.validityStartDate?.value;

  if (extract.validityEndDate)
    extract.validityEndDate.value = extract.validityEndDate?.value
      ? new Date(extract.validityEndDate.value)
      : extract.validityEndDate?.value;

  if (extract.issuedDate)
    extract.issuedDate.value = extract.issuedDate?.value
      ? new Date(extract.issuedDate.value)
      : extract.issuedDate?.value;

  if (extract.nextSurveyDate)
    extract.nextSurveyDate.value = extract.nextSurveyDate?.value
      ? new Date(extract.nextSurveyDate.value)
      : extract.nextSurveyDate?.value;

  if (extract.latestSurveyDate)
    extract.latestSurveyDate.value = extract.latestSurveyDate?.value
      ? new Date(extract.latestSurveyDate.value)
      : extract.latestSurveyDate?.value;

  return extract;
};

type RequestedDocumentsPageResponse = {
  data: Certificate[];
  nextPage: number | null;
};

export const uploadDocument = async (
  doc: Certificate,
  user: TUser | undefined,
  signal?: AbortSignal,
  onProgress?: (progress: DocumentExtractionTaskProgress) => void,
): Promise<DocumentMetadataExtract> => {
  const url = `${apiUrl}/documents`;
  const formData = new FormData();
  const body = {
    docName: doc.cachedFile?.name,
    uploadedBy: user?.email,
    source: doc.source,
  };

  formData.append("file", doc.cachedFile as File);
  formData.append("body", JSON.stringify(body));

  const response = await api.post(url, formData, { signal });
  log("REPO - uploadDocument - response", { status: response.status });
  let extract: DocumentMetadataExtract;
  if (isDocumentExtractionTaskAccepted(response.data)) {
    try {
      extract = await waitForDocumentExtraction(response.data, signal, onProgress);
    } catch (error) {
      if (signal?.aborted) {
        log("SERV - uploadDocument - cancelled");
        await cancelDocumentExtractionTask(response.data.taskId);
      }
      throw error;
    }
  } else {
    extract = response.data as DocumentMetadataExtract;
  }

  return normalizeExtractedDates(extract);
};

export const updateDocumentInformation = async (doc: Certificate, user: TUser | undefined) => {
  const url = `${apiUrl}/documents`;
  const formData = new FormData();
  const body: Certificate = {
    ...doc,
    uploadedBy: user?.email,
  };

  formData.append("body", JSON.stringify(body));

  const response = await api.put(url, formData);
  log("REPO - updateDocumentInformation - response", { status: response.status });
};

export const uploadDocAppendix = async (doc: Certificate, user: TUser | undefined) => {
  const url = `${apiUrl}/documents/appendix`;
  const formData = new FormData();
  const body: Certificate = {
    vesselCode: doc?.vesselCode,
    vesselName: doc?.vesselName,
    imoNumber: doc?.imoNumber,
    docName: doc?.docName,
    docType: doc?.docType,
    uploadedBy: user?.email,
    vesselMasterEmail: doc?.vesselMasterEmail || "",
    source: doc?.source,
    expiresOn: doc?.expiresOn,
    nextSurveyDate: doc?.nextSurveyDate,
    appendixFor: doc.appendixFor,
  };

  formData.append("file", doc.cachedFile as File);
  formData.append("body", JSON.stringify(body));

  const response = await api.post(url, formData);
  log("REPO - uploadDocAppendix - response", { status: response.status });
};

// If we need to check notifications are received by the VM we can edit the email address of one of the vessels in dev.
export const requestDocumentsForVessel = async (
  docs: RequestedDoc[],
  user: TUser | undefined,
  vessel: Vessel,
): Promise<string | null> => {
  log("SERV - requestDocumentsForVessel", { count: docs.length });
  try {
    const url = `${apiUrl}/documents/requests`;
    const req: RequestDocumentDto = { docs, user, vessel };

    const response = await api.post(url, req);
    log("REPO - requestDocumentsForVessel - response", { status: response.status });
  } catch (error: unknown) {
    logE(new Error("SERV - requestDocumentsForVessel - failed"));
    return error as string;
  }

  return null;
};

export const deleteDocument = async (s3Path: string) => {
  try {
    await api.get(`${apiUrl}/del-documents`, {
      params: {
        s3Path,
      },
    });
  } catch (error) {
    logE(new Error("SERV - deleteDocument - failed"));
  }
};

export async function getRequestedDocuments() {
  const requestedDocuments: Certificate[] = [];
  let page: number | null = 1;

  while (page !== null) {
    const result: AxiosResponse<RequestedDocumentsPageResponse> = await api.get(`${apiUrl}/extUserRequestedDocuments`, {
      params: { page },
    });

    requestedDocuments.push(...result.data.data);
    page = result.data.nextPage;
  }

  log("SERV - getRequestedDocuments - completed", { count: requestedDocuments.length });
  return requestedDocuments;
}
